import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string
  hint?: string
  error?: string | null
  leftAddon?: ReactNode
  rightAddon?: ReactNode
  wrapperClassName?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      hint,
      error,
      leftAddon,
      rightAddon,
      wrapperClassName,
      id,
      ...rest
    },
    ref
  ) => {
    const autoId = id ?? (label ? `in-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined)

    return (
      <div className={cn('grid gap-1.5', wrapperClassName)}>
        {label && (
          <label htmlFor={autoId} className="text-xs font-medium text-zinc-600">
            {label}
          </label>
        )}
        <div
          className={cn(
            'flex h-10 items-center gap-2 rounded-md border bg-white px-3 text-sm transition-colors',
            'focus-within:border-brand focus-within:ring-1 focus-within:ring-brand/40',
            error ? 'border-danger-border bg-danger-soft/40' : 'border-zinc-300'
          )}
        >
          {leftAddon && <span className="text-zinc-400">{leftAddon}</span>}
          <input
            ref={ref}
            id={autoId}
            className={cn(
              'h-full flex-1 border-none bg-transparent text-sm text-zinc-900 outline-none placeholder:text-zinc-400',
              className
            )}
            {...rest}
          />
          {rightAddon && <span className="text-zinc-400">{rightAddon}</span>}
        </div>
        {error ? (
          <p className="text-xs text-danger-fg">{error}</p>
        ) : hint ? (
          <p className="text-xs text-zinc-500">{hint}</p>
        ) : null}
      </div>
    )
  }
)

Input.displayName = 'Input'

export type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement> & {
  label?: string
  hint?: string
  error?: string | null
  wrapperClassName?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, label, hint, error, wrapperClassName, id, children, ...rest }, ref) => {
    const autoId = id ?? (label ? `sel-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined)
    return (
      <div className={cn('grid gap-1.5', wrapperClassName)}>
        {label && (
          <label htmlFor={autoId} className="text-xs font-medium text-zinc-600">
            {label}
          </label>
        )}
        <select
          ref={ref}
          id={autoId}
          className={cn(
            'h-10 rounded-md border bg-white px-3 text-sm text-zinc-900 outline-none transition-colors',
            'focus:border-brand focus:ring-1 focus:ring-brand/40',
            error ? 'border-danger-border' : 'border-zinc-300',
            className
          )}
          {...rest}
        >
          {children}
        </select>
        {error ? (
          <p className="text-xs text-danger-fg">{error}</p>
        ) : hint ? (
          <p className="text-xs text-zinc-500">{hint}</p>
        ) : null}
      </div>
    )
  }
)

Select.displayName = 'Select'
